import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { type CaptureCameraApi, type StartOptions, type CaptureFacing, useCaptureCamera } from './useCaptureCamera';

/**
 * US-03482 iPhone black-video RCA, G1 (decided 2026-11-03): ONE camera session for the whole capture.
 *
 * CaptureFlow owns the camera (`useFlowCamera`) and hands it down; every shot screen reads it with `useShotCamera`. A
 * shot screen is keyed per shot and re-mounts on every step — that re-mount no longer stops the tracks or asks the
 * browser again: only leaving the capture (or reaching review, where no shot needs the camera) stops them.
 *
 * Audio is asked for up front, for the whole session, when the script has any CLIP — so the first clip step does not
 * re-acquire to add the microphone (the moment WebKit was most likely to lose its frames). A refused microphone still
 * falls back to video-only (useCaptureCamera.acquire) and is not asked for again.
 */
const CaptureCameraContext = createContext<CaptureCameraApi | null>(null);

/** The flow-level owner. `active` false (no shot on screen) stops the tracks. */
export function useFlowCamera(active: boolean, sessionAudio: boolean): CaptureCameraApi {
  const cam = useCaptureCamera(active);
  const { stop } = cam;
  useEffect(() => {
    if (!active) stop();
  }, [active, stop]);
  const start = cam.start;
  const sessionStart = useMemo(
    // The session's mic is ACQUIRED up front (G1) but ON only on steps that record audio (F10).
    () => (facing: CaptureFacing, opts: StartOptions) => start(facing, { ...opts, audio: opts.audio || sessionAudio, micOn: opts.micOn ?? opts.audio }),
    [start, sessionAudio],
  );
  return { ...cam, start: sessionStart };
}

export function CaptureCameraProvider({ camera, children }: { camera: CaptureCameraApi; children: ReactNode }): JSX.Element {
  return <CaptureCameraContext.Provider value={camera}>{children}</CaptureCameraContext.Provider>;
}

/**
 * A shot screen's camera: the flow's shared session when there is one; otherwise (a screen rendered on its own — a
 * test, a standalone screen) a camera of its own, exactly as before.
 */
export function useShotCamera(): CaptureCameraApi {
  const shared = useContext(CaptureCameraContext);
  const own = useCaptureCamera(shared === null);
  return shared ?? own;
}
